import React, { useEffect, useRef, useState } from "react";
import AdjustLink from "web/react/components/adjust-link/adjust-link";
import SVGIcon, { SVGIconName } from "web/react/components/svg-icon/svg-icon";
import { Button } from "web/react/emo/button";
import { Text } from "web/react/emo/text";
import store from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import { RawAdjustLink } from "web/script/utils/adjust";
import browser from "web/script/utils/browser";
import {
    consumeRetailerReturnBannerArm,
    isRetailerReturnBannerEligible,
    markRetailerReturnBannerShown,
    TriggerPage,
} from "./retailer-return-banner-state";
import * as styles from "./retailer-return-banner.css";

interface Feature {
    icon: SVGIconName;
    title: string;
    description: string;
}

const ANALYTICS_CATEGORY = "web_to_app";
const ANALYTICS_LABEL = "pdp_modal";
const PRODUCT_PAGE_TYPES = ["product", "product_overlay"];
const PRODUCT_PAGE_SUB_TYPES = ["in_stock", "out_of_stock"];

function isOnProductPage(): boolean {
    return (
        PRODUCT_PAGE_TYPES.includes(environment.get("pageType")) &&
        PRODUCT_PAGE_SUB_TYPES.includes(environment.get("pageSubType"))
    );
}

type DismissalType = "maybe later button" | "dismissed";

interface RetailerReturnBannerViewProps {
    triggerPage: TriggerPage | null;
    onClose: (dismissalType: DismissalType) => void;
    onDownload: () => void;
}

export function RetailerReturnBannerView({
    triggerPage,
    onClose,
    onDownload,
}: RetailerReturnBannerViewProps): JSX.Element {
    const features: Feature[] = [
        {
            icon: "notification-outline",
            title: gettext("retailer_return_banner.feature_updates.title"),
            description: gettext("retailer_return_banner.feature_updates.description"),
        },
        {
            icon: "heart-outline",
            title: gettext("retailer_return_banner.feature_wishlists.title"),
            description: gettext("retailer_return_banner.feature_wishlists.description"),
        },
        {
            icon: "search-outline",
            title: gettext("retailer_return_banner.feature_search.title"),
            description: gettext("retailer_return_banner.feature_search.description"),
        },
    ];

    return (
        <div
            className={styles.overlay}
            role="presentation"
            onClick={(event): void => {
                if (event.target === event.currentTarget) {
                    onClose("dismissed");
                }
            }}
        >
            <div
                className={styles.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="retailer-return-banner-title"
                data-testid="retailer-return-banner"
            >
                <div className={styles.content}>
                    <div className={styles.header}>
                        <Text textStyle="subhead" id="retailer-return-banner-title">
                            {gettext("retailer_return_banner.title")}
                        </Text>
                        <Text textStyle="body-s" color="secondary">
                            {gettext("retailer_return_banner.description")}
                        </Text>
                    </div>
                    <div className={styles.features}>
                        {features.map(({ icon, title, description }) => (
                            <div className={styles.feature} key={icon}>
                                <SVGIcon name={icon} className={styles.featureIcon} />
                                <div className={styles.featureText}>
                                    <Text textStyle="body-s-bold">{title}</Text>
                                    <Text textStyle="body-xs" className={styles.featureDescription}>
                                        {description}
                                    </Text>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={styles.actions}>
                    <AdjustLink
                        adjustLink={RawAdjustLink.RetailerReturnAppPopup}
                        title={gettext("retailer_return_banner.cta")}
                        eventLabel={ANALYTICS_LABEL}
                        eventCustomData={{ web_to_app_trigger_page: triggerPage }}
                        beforeOnClick={onDownload}
                        primaryButton
                    />
                    <Button
                        variant="tertiary"
                        width="full"
                        title={gettext("retailer_return_banner.dismiss")}
                        titleClassName={styles.dismissTitle}
                        onClick={(): void => onClose("maybe later button")}
                    />
                </div>
            </div>
        </div>
    );
}

export function RetailerReturnBanner(): JSX.Element | null {
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const isMounted = useRef<boolean>(true);
    const triggerPageRef = useRef<TriggerPage | null>(null);

    useEffect(() => {
        isMounted.current = true;

        browser.addVisibilityListener((hidden: boolean) => {
            const triggerPage = hidden ? null : consumeRetailerReturnBannerArm();
            if (!triggerPage) {
                return;
            }

            const { topBannerReducer, customerCaptureOverlay } = store.getState();
            if (
                isMounted.current &&
                !topBannerReducer.isVisible &&
                !customerCaptureOverlay.open &&
                isOnProductPage() &&
                isRetailerReturnBannerEligible()
            ) {
                markRetailerReturnBannerShown();
                analytics.event({
                    category: ANALYTICS_CATEGORY,
                    label: ANALYTICS_LABEL,
                    action: "impression",
                    nonInteraction: true,
                    customData: { web_to_app_trigger_page: triggerPage },
                });
                triggerPageRef.current = triggerPage;
                setIsVisible(true);
            }
        });

        return (): void => {
            isMounted.current = false;
        };
    }, []);

    if (!isVisible) {
        return null;
    }

    return (
        <RetailerReturnBannerView
            triggerPage={triggerPageRef.current}
            onClose={(dismissalType): void => {
                analytics.event({
                    category: ANALYTICS_CATEGORY,
                    label: ANALYTICS_LABEL,
                    action: "dismissed",
                    customData: {
                        web_to_app_trigger_page: triggerPageRef.current,
                        dismissal_type: dismissalType,
                    },
                });
                userProfiler.saveRetailerReturnBannerDismissed();
                setIsVisible(false);
            }}
            onDownload={(): void => setIsVisible(false)}
        />
    );
}

export default RetailerReturnBanner;
